import { isPlatformBrowser } from "@angular/common";
import { Injectable, PLATFORM_ID, inject } from "@angular/core";
import { EMPTY, Observable, catchError, of, tap } from "rxjs";
import {
  SimpleXploreItemDTO,
  SimpleUserAccountDTO,
  UserAccountDTO,
  UserSavedXploreDTO
} from "../../shared/object/xplorer-be-parsed-classes";
import { environment } from "../../../environments/environment";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ToastService } from "../toast/toast.service";
import { ToastTextType } from "../../shared/object/toast";
import { TokenStorageService } from "../auth/token-storage.service";
import { AuthToken } from "../../shared/object/keycloak-classes";
import { AnalyticsService } from "../analytics/analytics.service";

const httpOptions = {
  headers: new HttpHeaders({'Content-Type': 'application/json'})
};


@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly favourtiesKey = 'user-favorites';
  private favorites: number[] = [];

  private readonly http: HttpClient = inject(HttpClient);
  private readonly tokenStorageService: TokenStorageService = inject(TokenStorageService);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);


  initSavedXplore() {
    const user: UserAccountDTO = this.tokenStorageService.getUser();

    if (user && user.userId) {
      this.http.get<UserSavedXploreDTO[]>(`${environment.API_URL}/saved-xplore`)
        .pipe(
          catchError(() => of([]))
        )
        .subscribe((savedItems) => {
          this.favorites = savedItems?.map((savedItem) => savedItem.xploreId) ?? [];
          this.persistFavorites();
        });
    }
  }

  addSavedXplore(id: number) {
    this.analyticsService.saveLikeAction(id);
    return this.http.post<UserSavedXploreDTO>(`${environment.API_URL}/saved-xplore/xplore/${id}`, {})
      .pipe(
        tap((savedItem) => {
          if (savedItem) {
            this.favorites.push(savedItem.xploreId);
            this.persistFavorites();
            this.toastService.showSuccessToast(ToastTextType.ADDED_TO_FAVORITES);
          }
        }),
        catchError((error) => {
          this.toastService.showErrorToast(`Error: ${error.error?.message ?? 'Unable to save to favorites. Please try again.'}`);
          return EMPTY;
        })
      )
      .subscribe();
  }

  removeFavorite(id: number) {
    this.http.delete<number>(`${environment.API_URL}/saved-xplore/xplore/${id}`)
      .pipe(
        tap((data) => {
          if (data === id) {
            this.favorites = this.favorites.filter((favoriteId) => favoriteId !== id);
            this.persistFavorites();
            this.toastService.showSuccessToast(ToastTextType.REMOVED_FROM_FAVORITES);
          }
        }),
        catchError((error) => {
          this.toastService.showErrorToast(`Error: ${error.error?.message ?? 'Unable to remove from favorites. Please try again.'}`);
          return EMPTY;
        })
      )
      .subscribe();
  }

  checkIfItemInFavorite(id: number) {
    return this.favorites?.includes(id);
  }

  getFavorites(): Observable<UserSavedXploreDTO[]> {
    return this.http.get<UserSavedXploreDTO[]>(`${environment.API_URL}/saved-xplore`);
  }

  getCurrentUser(): Observable<UserAccountDTO> {
    return this.http.get<UserAccountDTO>(`${environment.API_URL}/user/me`, httpOptions);
  }

  getCurrentUserContributedXploreItems(): Observable<SimpleXploreItemDTO[]> {
    return this.http.get<SimpleXploreItemDTO[]>(`${environment.API_URL}/user/me/contributions`);
  }

  upsertUserFromKeycloak(userInfo: UserAccountDTO): Observable<UserAccountDTO> {
    return this.http.post<UserAccountDTO>(`${environment.API_URL}/user/upsert/from-keycloak`, userInfo);
  }

  updateDisplayedName(displayedName: string) {
    return this.http.put<UserAccountDTO>(`${environment.API_URL}/user/update/displayed-name`, displayedName, httpOptions);
  }

  updateInstagramUsername(instagramUsername: string) {
    return this.http.put<UserAccountDTO>(`${environment.API_URL}/user/update/instagram-username`, instagramUsername, httpOptions);
  }

  getUserInfo(userId: number): Observable<SimpleUserAccountDTO> {
    return this.http.get<SimpleUserAccountDTO>(`${environment.API_URL}/user/${userId}/simple`);
  }

  getUserContributedXploreItems(userId: number): Observable<SimpleXploreItemDTO[]> {
    return this.http.get<SimpleXploreItemDTO[]>(`${environment.API_URL}/user/${userId}/contributions`);
  }

  private persistFavorites(): void {
    if (!this.isBrowser) {
      return;
    }
    const jsonSavedItems = JSON.stringify(this.favorites);
    localStorage.setItem(this.favourtiesKey, jsonSavedItems);
  }
}
